<template>
  <div class="w-full p-4 my-6">
    <smm-sub-nav />

    <div class="flex flex-wrap justify-between items-end gap-3 mb-4">
      <div>
        <h2 class="text-xl font-semibold text-gray-800">{{ $t('smm.today.title') }}</h2>
        <p class="text-sm text-gray-500 mt-1">{{ $t('smm.today.subtitle', { date: dayLabel }) }}</p>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <button
          type="button"
          class="px-2.5 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
          :title="$t('smm.today.prev_day')"
          @click="shiftDay(-1)"
        >
          ←
        </button>
        <input
          v-model="selectedDate"
          type="date"
          class="border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm"
          @change="onDatePicked"
        >
        <button
          type="button"
          class="px-2.5 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
          :title="$t('smm.today.next_day')"
          @click="shiftDay(1)"
        >
          →
        </button>
        <button
          v-if="!isToday"
          type="button"
          class="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
          @click="goToday"
        >
          {{ $t('smm.today.go_today') }}
        </button>
        <button
          type="button"
          class="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
          @click="load"
        >
          {{ $t('common.refresh') }}
        </button>
      </div>
    </div>

    <p v-if="error" class="text-sm text-red-600 mb-3">{{ error }}</p>
    <div v-if="loading" class="p-8 text-center text-gray-500">{{ $t('common.loading') }}</div>
    <div v-else-if="!tasks.length" class="p-8 text-center text-gray-500 bg-white rounded-lg shadow-md">
      {{ $t('smm.today.empty') }}
    </div>

    <div v-else class="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-4">
      <div class="space-y-2">
        <button
          v-for="task in tasks"
          :key="task.id"
          type="button"
          class="w-full text-left bg-white rounded-lg border p-3 transition"
          :class="selectedId === task.id
            ? 'border-gray-900 shadow-md'
            : 'border-gray-100 hover:border-gray-300'"
          @click="selectTask(task.id)"
        >
          <div class="flex gap-3">
            <div
              class="w-14 h-14 rounded-md bg-gray-100 overflow-hidden shrink-0 border border-gray-100"
            >
              <img
                v-if="taskThumb(task)"
                :src="taskThumb(task)"
                :alt="task.media_content?.title || ''"
                class="w-full h-full object-cover"
              >
              <div
                v-else
                class="w-full h-full flex items-center justify-center text-[10px] text-gray-400 px-1 text-center"
              >
                {{ $t('smm.today.no_photo') }}
              </div>
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex justify-between gap-2">
                <p class="font-medium text-gray-800 truncate">
                  {{ task.media_content?.title || $t('smm.today.no_product') }}
                </p>
                <span class="shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 h-fit">
                  {{ $t(`smm.today.status.${task.status}`) }}
                </span>
              </div>
              <p class="text-sm text-gray-600 truncate mt-0.5">
                {{ task.social_network?.name || task.platform?.name || '—' }}
                <span v-if="task.format"> · {{ task.format }}</span>
              </p>
              <p v-if="task.angle" class="text-xs text-gray-500 line-clamp-2 mt-0.5">{{ task.angle }}</p>
            </div>
          </div>
        </button>
      </div>

      <div v-if="active" class="bg-white rounded-lg border border-gray-100 shadow-sm p-4 sm:p-5 space-y-4">
        <div>
          <h3 class="text-lg font-semibold text-gray-900">
            {{ active.social_network?.name || active.platform?.name || '—' }}
            <span v-if="active.format" class="text-gray-500 font-normal">· {{ active.format }}</span>
          </h3>
          <p class="text-sm text-gray-500 mt-1">
            {{ active.goal || '—' }} · {{ active.time_slot || '—' }}
            · {{ active.shop?.name || '—' }}
          </p>
        </div>

        <!-- Product -->
        <div class="rounded-lg border border-gray-200 bg-gray-50 p-3 sm:p-4">
          <p class="text-xs uppercase tracking-wide text-gray-500 mb-2">{{ $t('smm.today.product') }}</p>
          <div v-if="active.media_content" class="flex gap-3">
            <div class="w-20 h-20 sm:w-24 sm:h-24 rounded-md overflow-hidden bg-white border border-gray-200 shrink-0">
              <img
                v-if="productCoverUrl"
                :src="productCoverUrl"
                :alt="active.media_content.title"
                class="w-full h-full object-cover"
              >
              <div v-else class="w-full h-full flex items-center justify-center text-xs text-gray-400">
                {{ $t('smm.today.no_photo') }}
              </div>
            </div>
            <div class="min-w-0">
              <p class="font-semibold text-gray-900 leading-snug">{{ active.media_content.title }}</p>
              <p class="text-sm text-gray-600 mt-1">
                {{ active.media_content.shop?.name || active.shop?.name || '—' }}
                <span v-if="active.media_content.collection"> · {{ active.media_content.collection }}</span>
              </p>
              <p v-if="active.media_content.category" class="text-xs text-gray-500 mt-0.5">
                {{ active.media_content.category }}
              </p>
              <a
                v-if="active.media_content.listing_url || active.media_content.link"
                :href="active.media_content.listing_url || active.media_content.link"
                target="_blank"
                rel="noopener"
                class="inline-block text-xs text-blue-600 hover:underline mt-1.5"
              >
                {{ $t('smm.today.open_listing') }}
              </a>
            </div>
          </div>
          <p v-else class="text-sm text-gray-500">{{ $t('smm.today.no_product') }}</p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div>
            <p class="text-gray-500">{{ $t('smm.today.angle') }}</p>
            <p class="font-medium whitespace-pre-line">{{ active.angle || '—' }}</p>
          </div>
          <div>
            <p class="text-gray-500">{{ $t('smm.today.selected_photos') }}</p>
            <p class="font-medium">{{ selectedMediaIds.length }}</p>
          </div>
        </div>

        <div v-if="active.brief" class="text-sm">
          <p class="text-gray-500 mb-1">{{ $t('smm.today.brief') }}</p>
          <p class="text-gray-800 whitespace-pre-line">{{ active.brief }}</p>
        </div>

        <!-- Product media picker -->
        <div v-if="productImageFiles.length">
          <div class="flex flex-wrap items-end justify-between gap-2 mb-2">
            <div>
              <p class="text-sm font-medium text-gray-700">{{ $t('smm.today.pick_photos') }}</p>
              <p class="text-xs text-gray-500 mt-0.5">{{ $t('smm.today.pick_photos_hint') }}</p>
            </div>
            <button
              v-if="selectedMediaIds.length"
              type="button"
              class="text-xs text-gray-500 hover:text-gray-800"
              @click="selectedMediaIds = []"
            >
              {{ $t('smm.today.clear_selection') }}
            </button>
          </div>
          <div class="grid grid-cols-3 sm:grid-cols-4 gap-2">
            <button
              v-for="file in productImageFiles"
              :key="file.id"
              type="button"
              class="relative aspect-square rounded-md overflow-hidden border-2 bg-gray-100 transition"
              :class="isSelected(file.id)
                ? 'border-blue-600 ring-2 ring-blue-200'
                : 'border-transparent hover:border-gray-300'"
              @click="toggleMedia(file.id)"
            >
              <img :src="file.url" :alt="file.original_name || ''" class="w-full h-full object-cover">
              <span
                v-if="isSelected(file.id)"
                class="absolute top-1 right-1 w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-semibold flex items-center justify-center"
              >
                {{ selectionIndex(file.id) }}
              </span>
            </button>
          </div>
        </div>
        <p v-else-if="active.media_content" class="text-sm text-gray-500">
          {{ $t('smm.today.no_product_photos') }}
        </p>

        <div>
          <label class="text-sm text-gray-600">{{ $t('smm.today.media_description') }}</label>
          <textarea
            v-model="mediaDescription"
            rows="4"
            class="w-full border rounded-lg p-2.5 mt-1 text-sm resize-y"
            :placeholder="$t('smm.today.media_description_placeholder')"
          />
          <button
            type="button"
            class="mt-2 px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
            :disabled="saving"
            @click="saveMedia"
          >
            {{ $t('common.save') }}
          </button>
        </div>

        <div class="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            class="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            :disabled="busy"
            @click="generateContent"
          >
            {{ busy === 'generate' ? $t('common.saving') : $t('smm.today.generate_content') }}
          </button>
          <button
            type="button"
            class="px-4 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            :disabled="busy || !active.generated_content"
            @click="publish"
          >
            {{ busy === 'publish' ? $t('common.saving') : $t('smm.today.publish') }}
          </button>
        </div>

        <div v-if="active.generated_content" class="space-y-3">
          <p class="text-sm font-medium text-gray-700">{{ $t('smm.today.generated') }}</p>
          <div v-if="generatedSections(active.generated_content).length" class="space-y-3">
            <div v-for="section in generatedSections(active.generated_content)" :key="section">
              <p class="text-xs uppercase tracking-wide text-gray-500 mb-1">{{ sectionLabel(section) }}</p>
              <pre class="bg-gray-50 rounded-lg p-3 overflow-x-auto text-xs whitespace-pre-wrap">{{ pretty(active.generated_content[section]) }}</pre>

              <div
                v-if="section === 'data' && flattenFields(active.generated_content.data).length"
                class="mt-2 space-y-2"
              >
                <div
                  v-for="field in flattenFields(active.generated_content.data)"
                  :key="field.key"
                  class="rounded-lg border border-gray-200 bg-white p-2.5"
                >
                  <div class="flex items-center justify-between gap-2">
                    <p class="text-[11px] uppercase tracking-wide text-gray-400 truncate">{{ field.label }}</p>
                    <div class="shrink-0 flex items-center gap-1.5">
                      <span v-if="copiedKey === field.key" class="text-[10px] text-green-600">{{ $t('common.copied') }}</span>
                      <button
                        type="button"
                        class="text-gray-400 hover:text-gray-800"
                        :title="$t('common.copy')"
                        @click="copyField(field.value, field.key)"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <p class="text-sm text-gray-800 whitespace-pre-wrap break-words mt-1">{{ field.value || '—' }}</p>
                </div>
              </div>
            </div>
          </div>
          <pre v-else class="bg-gray-50 rounded-lg p-3 overflow-x-auto text-xs whitespace-pre-wrap">{{ pretty(active.generated_content) }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SmmSubNav from '@/components/smm/SmmSubNav.vue'

const MAX_AI_IMAGES = 4

function localDateString(d = new Date()) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function parseLocalDate(iso) {
  const [y, m, d] = String(iso).split('-').map(Number)
  return new Date(y, m - 1, d)
}

export default {
  components: { SmmSubNav },
  data: () => ({
    tasks: [],
    selectedId: null,
    active: null,
    mediaDescription: '',
    selectedMediaIds: [],
    selectedDate: localDateString(),
    loading: true,
    detailLoading: false,
    saving: false,
    busy: null,
    error: null,
    loadSeq: 0,
    detailSeq: 0,
    copiedKey: null,
  }),
  computed: {
    isToday() {
      return this.selectedDate === localDateString()
    },
    dayLabel() {
      return parseLocalDate(this.selectedDate).toLocaleDateString(
        this.$i18n.locale === 'ru' ? 'ru-RU' : 'en-US',
        {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        },
      )
    },
    productImageFiles() {
      const files = this.active?.media_content?.files || []
      return files.filter((f) => {
        if (f.type === 'video') return false
        const mime = (f.mime_type || '').toLowerCase()
        return !mime || mime.startsWith('image/')
      })
    },
    productCoverUrl() {
      if (this.selectedMediaIds.length) {
        const first = this.productImageFiles.find((f) => f.id === this.selectedMediaIds[0])
        if (first?.url) return first.url
      }
      return this.productImageFiles[0]?.url || null
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    pretty(v) {
      try {
        return JSON.stringify(v, null, 2)
      } catch (_) {
        return String(v)
      }
    },
    generatedSections(payload) {
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return []
      return ['metadata', 'data', 'content', 'platform_specific'].filter((key) => key in payload)
    },
    sectionLabel(section) {
      const key = `smm.today.envelope.${section}`
      return this.$te(key) ? this.$t(key) : section
    },
    humanizeKey(path) {
      return String(path)
        .split('.')
        .map((seg) =>
          seg
            .replace(/\[(\d+)\]/g, ' $1')
            .replace(/[_-]+/g, ' ')
            .trim()
            .replace(/\b\w/g, (c) => c.toUpperCase()),
        )
        .join(' · ')
    },
    flattenFields(value, prefix = '') {
      const out = []
      if (value == null) return out

      if (Array.isArray(value)) {
        const allPrimitive = value.every((v) => v == null || typeof v !== 'object')
        if (allPrimitive) {
          const items = value.filter((v) => v != null && String(v).trim() !== '')
          const looksLikeTags = items.some((v) => String(v).trim().startsWith('#'))
          out.push({
            key: prefix || 'items',
            label: this.humanizeKey(prefix || 'items'),
            value: items.join(looksLikeTags ? ' ' : '\n'),
          })
        } else {
          value.forEach((v, i) => {
            out.push(...this.flattenFields(v, `${prefix}[${i + 1}]`))
          })
        }
        return out
      }

      if (typeof value === 'object') {
        for (const [k, v] of Object.entries(value)) {
          const label = prefix ? `${prefix}.${k}` : k
          if (v != null && typeof v === 'object') {
            out.push(...this.flattenFields(v, label))
          } else {
            out.push({
              key: label,
              label: this.humanizeKey(label),
              value: v == null ? '' : String(v),
            })
          }
        }
        return out
      }

      out.push({
        key: prefix || 'value',
        label: this.humanizeKey(prefix || 'value'),
        value: String(value),
      })
      return out
    },
    async copyField(text, key) {
      const value = text == null ? '' : String(text)
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value)
        } else {
          const ta = document.createElement('textarea')
          ta.value = value
          ta.style.position = 'fixed'
          ta.style.opacity = '0'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.copiedKey = key
        setTimeout(() => {
          if (this.copiedKey === key) this.copiedKey = null
        }, 1500)
      } catch (_) {
        this.error = this.$t('common.copy_failed')
      }
    },
    shiftDay(delta) {
      const d = parseLocalDate(this.selectedDate)
      d.setDate(d.getDate() + delta)
      this.selectedDate = localDateString(d)
      this.selectedId = null
      this.active = null
      this.load()
    },
    goToday() {
      this.selectedDate = localDateString()
      this.selectedId = null
      this.active = null
      this.load()
    },
    onDatePicked() {
      this.selectedId = null
      this.active = null
      this.load()
    },
    taskThumb(task) {
      const files = task?.media_content?.files || []
      const img = files.find((f) => f.type !== 'video' && (!(f.mime_type) || String(f.mime_type).startsWith('image/')))
      return img?.url || null
    },
    isSelected(id) {
      return this.selectedMediaIds.includes(id)
    },
    selectionIndex(id) {
      return this.selectedMediaIds.indexOf(id) + 1
    },
    toggleMedia(id) {
      const idx = this.selectedMediaIds.indexOf(id)
      if (idx >= 0) {
        this.selectedMediaIds = this.selectedMediaIds.filter((x) => x !== id)
        return
      }
      if (this.selectedMediaIds.length >= MAX_AI_IMAGES) {
        this.error = this.$t('smm.today.max_photos', { n: MAX_AI_IMAGES })
        return
      }
      this.error = null
      this.selectedMediaIds = [...this.selectedMediaIds, id]
    },
    applyTask(task) {
      this.active = task
      this.mediaDescription = task?.media_description || ''
      const saved = Array.isArray(task?.selected_media_file_ids)
        ? task.selected_media_file_ids.map((x) => Number(x)).filter(Boolean)
        : []
      const allowed = new Set(this.productImageFiles.map((f) => f.id))
      this.selectedMediaIds = saved.filter((id) => allowed.has(id)).slice(0, MAX_AI_IMAGES)
    },
    async load() {
      const seq = ++this.loadSeq
      this.loading = true
      this.error = null
      try {
        const { data } = await axios.get('/api/v1/smm/tasks', {
          params: {
            workspace: 1,
            date: this.selectedDate,
          },
        })
        if (seq !== this.loadSeq) return

        this.tasks = Array.isArray(data?.data) ? data.data : []
        let nextId = this.selectedId
        if (nextId && !this.tasks.some((t) => t.id === nextId)) {
          nextId = null
        }
        if (!nextId && this.tasks.length) {
          nextId = this.tasks[0].id
        }

        if (!this.tasks.length) {
          this.selectedId = null
          this.active = null
          this.selectedMediaIds = []
          return
        }

        // List is ready — don't keep the full-page spinner on the detail fetch.
        this.loading = false
        if (nextId) {
          await this.selectTask(nextId)
        }
      } catch (e) {
        if (seq !== this.loadSeq) return
        this.error = e.response?.data?.message || e.message || this.$t('common.error')
        this.tasks = []
        this.active = null
      } finally {
        if (seq === this.loadSeq) {
          this.loading = false
        }
      }
    },
    async selectTask(id) {
      const seq = ++this.detailSeq
      this.selectedId = id
      this.error = null
      this.detailLoading = true

      // Optimistic list data while detail loads
      const listed = this.tasks.find((t) => t.id === id)
      if (listed && (!this.active || this.active.id !== id)) {
        this.applyTask(listed)
      }

      try {
        const { data } = await axios.get(`/api/v1/smm/tasks/${id}`)
        if (seq !== this.detailSeq) return
        this.applyTask(data.data)
      } catch (e) {
        if (seq !== this.detailSeq) return
        this.error = e.response?.data?.message || e.message || this.$t('common.error')
      } finally {
        if (seq === this.detailSeq) {
          this.detailLoading = false
        }
      }
    },
    async saveMedia() {
      if (!this.active) return
      this.saving = true
      try {
        const { data } = await axios.patch(`/api/v1/smm/tasks/${this.active.id}`, {
          media_description: this.mediaDescription || null,
          selected_media_file_ids: this.selectedMediaIds,
        })
        this.applyTask(data.data)
        const keepId = data.data.id
        this.selectedId = keepId
        await this.load()
      } catch (e) {
        this.error = e.response?.data?.message || this.$t('common.save_error')
      } finally {
        this.saving = false
      }
    },
    async generateContent() {
      if (!this.active) return
      this.busy = 'generate'
      this.error = null
      try {
        const { data } = await axios.post(`/api/v1/smm/tasks/${this.active.id}/generate-content`, {
          media_description: this.mediaDescription || null,
          selected_media_file_ids: this.selectedMediaIds,
        })
        this.applyTask(data.data)
        this.selectedId = data.data.id
        await this.load()
      } catch (e) {
        this.error = e.response?.data?.message || this.$t('common.error')
      } finally {
        this.busy = null
      }
    },
    async publish() {
      if (!this.active || !confirm(this.$t('smm.today.publish_confirm'))) return
      this.busy = 'publish'
      this.error = null
      try {
        await axios.post(`/api/v1/smm/tasks/${this.active.id}/publish`)
        this.selectedId = null
        this.active = null
        await this.load()
      } catch (e) {
        this.error = e.response?.data?.message || this.$t('common.error')
      } finally {
        this.busy = null
      }
    },
  },
}
</script>
